import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link as RouterLink,
  useLocation as useRouterLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CoPresentOutlinedIcon from '@mui/icons-material/CoPresentOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PdfViewer from './PdfViewer';
import SpreadsheetViewer from './SpreadsheetViewer';
import ViewerLoading from './ViewerLoading';
import LegislativeMapTour from './LegislativeMapTour';
import LocationLanding, { analysisTitle } from './LocationLanding';
import OpportunityCalculator from './opportunity_calculator/OpportunityCalculator';
import RiskCalculator from './risk_calculator/RiskCalculator';
import { partnerOrgName } from './partnerLogos';
import {
  filesForCategory,
  isIndividualLegislativeMap,
  locationHasLegislativeMaps,
  readTourDismissed,
  shouldShowLegislativeTour,
  stepsWithoutDataSources,
  dataSourcesStepIndex,
  TOUR_STEPS,
  writeTourDismissed,
} from './legislativeTour';
import {
  desertTourSteps,
  isDesertMapItem,
  locationHasDesertMaps,
  readDesertTourDismissed,
  shouldShowDesertTour,
  writeDesertTourDismissed,
} from './desertTour';
import {
  isWardMapItem,
  locationHasWardMaps,
  readWardTourDismissed,
  shouldShowWardTour,
  WARD_TOUR_STEPS,
  writeWardTourDismissed,
} from './wardTour';
import {
  isOverlapMapItem,
  locationHasOverlapMaps,
  overlapTourSteps,
  readOverlapTourDismissed,
  shouldShowOverlapTour,
  writeOverlapTourDismissed,
} from './overlapTour';
import {
  ENROLLMENT_TOUR_STEPS,
  isEnrollmentMapItem,
  isQualityMapItem,
  locationHasEnrollmentMaps,
  locationHasQualityMaps,
  QUALITY_TOUR_STEPS,
  readEnrollmentTourDismissed,
  readQualityTourDismissed,
  shouldShowEnrollmentTour,
  shouldShowQualityTour,
  writeEnrollmentTourDismissed,
  writeQualityTourDismissed,
} from './schoolInfoTour';
import {
  geographicTourSteps,
  isGeographicMapItem,
  locationHasGeographicMaps,
  readGeographicTourDismissed,
  shouldShowGeographicTour,
  writeGeographicTourDismissed,
} from './geographicTour';

const RAIL_WIDTH_PX = 300;
const SIGNED_URL_TTL_MS = 12 * 60 * 1000;
const PDF_CONTENT_TYPE = 'application/pdf';
const HTML_CONTENT_TYPE = 'text/html';
const MAPS_SECTION_KEY = 'maps';

// Live, client-rendered map components (see city_explorer_data_routes.py and
// ./maps/index.js's LIVE_MAP_COMPONENTS). Loaded with React.lazy/dynamic
// import() rather than a static import so react-leaflet/leaflet - and the
// leaflet.css they pull in - are only fetched for locations that actually
// use a live map, and so importing ExplorerView.js in tests that never
// render a map doesn't require an ESM-only package Jest can't transform.
const LIVE_MAP_LOADERS = {
  SchoolDistrictsMap: () => import('./maps/SchoolDistrictsMap'),
  DistrictOpportunityMap: () => import('./maps/DistrictOpportunityMap'),
  CountiesMap: () => import('./maps/CountiesMap'),
  CharterEnrollmentRiskMap: () => import('./maps/CharterEnrollmentRiskMap'),
  WardMetricsMap: () => import('./maps/WardMetricsMap'),
  CharterDesertMap: () => import('./maps/CharterDesertMap'),
  DistrictQualityDesertMap: () => import('./maps/DistrictQualityDesertMap'),
  CombinedDesertMap: () => import('./maps/CombinedDesertMap'),
  CharterOverlapMap: () => import('./maps/CharterOverlapMap'),
  StateDynamicsMap: () => import('./maps/StateDynamicsMap'),
  SchoolLocationsMap: () => import('./maps/SchoolLocationsMap'),
};
const liveMapComponentCache = {};
const getLiveMapComponent = (componentId) => {
  if (!LIVE_MAP_LOADERS[componentId]) return null;
  if (!liveMapComponentCache[componentId]) {
    liveMapComponentCache[componentId] = React.lazy(LIVE_MAP_LOADERS[componentId]);
  }
  return liveMapComponentCache[componentId];
};

const RESOURCE_SECTIONS = [
  {
    kind: 'presentation',
    title: 'Analysis',
    icon: CoPresentOutlinedIcon,
    placement: 'beforeMaps',
    collapsible: false,
  },
  {
    kind: 'calculator',
    title: 'Calculators',
    icon: CalculateOutlinedIcon,
    placement: 'afterMaps',
    collapsible: false,
  },
  { kind: 'data', title: 'Data Sets', icon: TableChartOutlinedIcon, placement: 'afterMaps', collapsible: true },
  {
    kind: 'documentation',
    title: 'Data Sources and Documentation',
    icon: MenuBookOutlinedIcon,
    placement: 'afterMaps',
    collapsible: true,
  },
];

const isGradeBanded = (category) =>
  Boolean(category?.files?.length) && category.files.every((file) => file.gradeBand);

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SectionHeading = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: '0.08em' }}
  >
    {children}
  </Typography>
);

const RailSection = ({ title, collapsible = false, open = true, onToggle, children }) => (
  <Box sx={{ mt: 2.5 }}>
    <Divider sx={{ mb: collapsible ? 1 : 1.5 }} />
    {collapsible ? (
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          px: 0.5,
          py: 0.5,
          border: 'none',
          background: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <SectionHeading>{title}</SectionHeading>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transition: 'transform 0.2s ease-in-out',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
    ) : (
      <SectionHeading>{title}</SectionHeading>
    )}
    {collapsible ? <Collapse in={open}>{children}</Collapse> : children}
  </Box>
);

const ExplorerView = ({
  location,
  loadingLocation = false,
  locationError = null,
  basePath,
  categoryId,
  fileId,
  resourceId,
  fetchMapUrl,
  fetchResourceUrl,
  fetchSummary,
  footerOffsetPx = 72,
  showAllLocationsLink = true,
  allLocationsPath = '/city-explorer',
  toolbarExtra = null,
  onShareCalculator,
}) => {
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();

  const [viewUrl, setViewUrl] = useState(null);
  const [viewError, setViewError] = useState(null);
  const [urlLoading, setUrlLoading] = useState(true);
  const [mapFrameLoading, setMapFrameLoading] = useState(true);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [railOpen, setRailOpen] = useState(true);
  const [openSections, setOpenSections] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [legislativeTourDismissed, setLegislativeTourDismissed] = useState(() => readTourDismissed());
  const [desertTourDismissed, setDesertTourDismissed] = useState(() => readDesertTourDismissed());
  const [wardTourDismissed, setWardTourDismissed] = useState(() => readWardTourDismissed());
  const [overlapTourDismissed, setOverlapTourDismissed] = useState(() => readOverlapTourDismissed());
  const [enrollmentTourDismissed, setEnrollmentTourDismissed] = useState(() =>
    readEnrollmentTourDismissed()
  );
  const [qualityTourDismissed, setQualityTourDismissed] = useState(() => readQualityTourDismissed());
  const [geographicTourDismissed, setGeographicTourDismissed] = useState(() =>
    readGeographicTourDismissed()
  );
  const [tourReplay, setTourReplay] = useState(false);
  const [calculatorHighlightSources, setCalculatorHighlightSources] = useState(false);

  const urlCache = useRef(new Map());
  const locationId = location?.id;

  const categories = useMemo(() => location?.categories || [], [location]);
  const resources = useMemo(() => location?.resources || [], [location]);
  const includeDataSourcesTour = resources.some(
    (resource) => resource.kind === 'documentation' || resource.kind === 'data'
  );

  const documentationResource = useMemo(
    () => resources.find((resource) => resource.kind === 'documentation') || null,
    [resources]
  );

  const isLanding = !categoryId && !resourceId;

  const activeItem = useMemo(() => {
    if (isLanding) return null;

    if (resourceId) {
      const resource = resources.find((r) => r.id === resourceId);
      return resource ? { type: 'resource', resource } : null;
    }

    if (categoryId) {
      const category = categories.find((c) => c.id === categoryId);
      const files = filesForCategory(category);
      if (files.length) {
        const file = files.find((f) => f.id === fileId) || files[0];
        return { type: 'map', category, file };
      }
    }

    return null;
  }, [isLanding, resourceId, categoryId, fileId, resources, categories]);

  const pathForItem = useCallback(
    (item) =>
      item.type === 'resource'
        ? `${basePath}/documents/${item.resource.id}`
        : `${basePath}/${item.category.id}/${item.file.id}`,
    [basePath]
  );

  useEffect(() => {
    if (!basePath || isLanding || loadingLocation || !location) return;
    if (activeItem) {
      const canonical = pathForItem(activeItem);
      if (routerLocation.pathname !== canonical) {
        navigate(canonical, { replace: true });
      }
      return;
    }
    if (routerLocation.pathname !== basePath) {
      navigate(basePath, { replace: true });
    }
  }, [
    isLanding,
    activeItem,
    pathForItem,
    routerLocation.pathname,
    navigate,
    basePath,
    loadingLocation,
    location,
  ]);

  // Cross-links between the Risk Calculator and the charter risk map, only
  // when both are part of this location (or this shared link).
  const riskCalculatorPath = useMemo(() => {
    const resource = resources.find((r) => r.kind === 'calculator' && r.calculatorType === 'risk');
    return resource ? `${basePath}/documents/${resource.id}` : null;
  }, [resources, basePath]);
  const riskMapPath = useMemo(() => {
    for (const category of categories) {
      const file = (category.files || []).find((f) => f.component === 'CharterEnrollmentRiskMap');
      if (file) return `${basePath}/${category.id}/${file.id}`;
    }
    return null;
  }, [categories, basePath]);

  const activeSectionKey = activeItem
    ? activeItem.type === 'map'
      ? MAPS_SECTION_KEY
      : activeItem.resource.kind
    : null;

  useEffect(() => {
    if (!activeSectionKey) return;
    setOpenSections((prev) => (prev[activeSectionKey] ? prev : { ...prev, [activeSectionKey]: true }));
  }, [activeSectionKey]);

  useEffect(() => {
    if (!isLanding) return;
    setOpenSections((prev) => ({
      ...prev,
      [MAPS_SECTION_KEY]: true,
      presentation: true,
    }));
  }, [isLanding]);

  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const legislativeSteps = includeDataSourcesTour ? TOUR_STEPS : stepsWithoutDataSources(TOUR_STEPS);
  const wardSteps = includeDataSourcesTour ? WARD_TOUR_STEPS : stepsWithoutDataSources(WARD_TOUR_STEPS);
  const legislativeDataSourcesIndex = dataSourcesStepIndex(legislativeSteps);
  const wardDataSourcesIndex = dataSourcesStepIndex(wardSteps);

  const hasLegislativeCategory = locationHasLegislativeMaps(categories);
  const hasDesertCategory = locationHasDesertMaps(categories);
  const hasWardCategory = locationHasWardMaps(categories);
  const hasOverlapCategory = locationHasOverlapMaps(categories);
  const hasEnrollmentCategory = locationHasEnrollmentMaps(categories);
  const hasQualityCategory = locationHasQualityMaps(categories);
  const hasGeographicCategory = locationHasGeographicMaps(categories);
  const viewingLegislativeMap = isIndividualLegislativeMap(activeItem);
  const viewingDesertMap = isDesertMapItem(activeItem);
  const viewingWardMap = isWardMapItem(activeItem);
  const viewingOverlapMap = isOverlapMapItem(activeItem);
  const viewingEnrollmentMap = isEnrollmentMapItem(activeItem);
  const viewingQualityMap = isQualityMapItem(activeItem);
  const viewingGeographicMap = isGeographicMapItem(activeItem);
  const mapFrameReady =
    activeItem?.type === 'map' && !urlLoading && Boolean(viewUrl) && !mapFrameLoading && !viewError;
  const showLegislativeTour = shouldShowLegislativeTour({
    hasLegislativeCategory,
    viewingLegislativeMap,
    mapLoaded: mapFrameReady,
    isFullscreen,
    dismissed: legislativeTourDismissed,
    replaying: tourReplay,
  });
  const showDesertTour = shouldShowDesertTour({
    hasDesertCategory,
    viewingDesertMap,
    mapLoaded: mapFrameReady,
    isFullscreen,
    dismissed: desertTourDismissed,
    replaying: tourReplay,
  });
  const showWardTour = shouldShowWardTour({
    hasWardCategory,
    viewingWardMap,
    mapLoaded: mapFrameReady,
    isFullscreen,
    dismissed: wardTourDismissed,
    replaying: tourReplay,
  });
  const showOverlapTour = shouldShowOverlapTour({
    hasOverlapCategory,
    viewingOverlapMap,
    mapLoaded: mapFrameReady,
    isFullscreen,
    dismissed: overlapTourDismissed,
    replaying: tourReplay,
  });
  const showEnrollmentTour = shouldShowEnrollmentTour({
    hasEnrollmentCategory,
    viewingEnrollmentMap,
    mapLoaded: mapFrameReady,
    isFullscreen,
    dismissed: enrollmentTourDismissed,
    replaying: tourReplay,
  });
  const showQualityTour = shouldShowQualityTour({
    hasQualityCategory,
    viewingQualityMap,
    mapLoaded: mapFrameReady,
    isFullscreen,
    dismissed: qualityTourDismissed,
    replaying: tourReplay,
  });
  const showGeographicTour = shouldShowGeographicTour({
    hasGeographicCategory,
    viewingGeographicMap,
    mapLoaded: mapFrameReady,
    isFullscreen,
    dismissed: geographicTourDismissed,
    replaying: tourReplay,
  });
  const desertSteps = useMemo(
    () => (viewingDesertMap ? desertTourSteps(activeItem.category) : []),
    [viewingDesertMap, activeItem]
  );
  const overlapSteps = useMemo(
    () => (viewingOverlapMap ? overlapTourSteps(activeItem.category) : []),
    [viewingOverlapMap, activeItem]
  );
  const geographicSteps = useMemo(
    () =>
      viewingGeographicMap
        ? geographicTourSteps(activeItem.category, { includeDataSources: includeDataSourcesTour })
        : [],
    [viewingGeographicMap, activeItem, includeDataSourcesTour]
  );
  const geographicDataSourcesIndex = dataSourcesStepIndex(geographicSteps);
  const highlightSourcesRail =
    (showLegislativeTour && legislativeDataSourcesIndex >= 0 && tourStep === legislativeDataSourcesIndex) ||
    (showWardTour && wardDataSourcesIndex >= 0 && tourStep === wardDataSourcesIndex) ||
    (showGeographicTour && geographicDataSourcesIndex >= 0 && tourStep === geographicDataSourcesIndex) ||
    calculatorHighlightSources;
  const tourKind = viewingLegislativeMap
    ? 'legislative'
    : viewingDesertMap
      ? 'desert'
      : viewingWardMap
        ? 'ward'
        : viewingOverlapMap
          ? 'overlap'
          : viewingEnrollmentMap
            ? 'enrollment'
            : viewingQualityMap
              ? 'quality'
              : viewingGeographicMap
                ? 'geographic'
                : null;
  const showReplayButton =
    (viewingLegislativeMap && legislativeTourDismissed && !showLegislativeTour) ||
    (viewingDesertMap && desertTourDismissed && !showDesertTour) ||
    (viewingWardMap && wardTourDismissed && !showWardTour) ||
    (viewingOverlapMap && overlapTourDismissed && !showOverlapTour) ||
    (viewingEnrollmentMap && enrollmentTourDismissed && !showEnrollmentTour) ||
    (viewingQualityMap && qualityTourDismissed && !showQualityTour) ||
    (viewingGeographicMap && geographicTourDismissed && !showGeographicTour);

  useEffect(() => {
    setTourStep(0);
    setTourReplay(false);
  }, [tourKind]);

  useEffect(() => {
    if (!highlightSourcesRail) return undefined;
    setRailOpen(true);
    setOpenSections((prev) => ({ ...prev, documentation: true, data: true }));
    return undefined;
  }, [highlightSourcesRail]);

  const dismissActiveTour = () => {
    if (viewingDesertMap) {
      writeDesertTourDismissed();
      setDesertTourDismissed(true);
    } else if (viewingWardMap) {
      writeWardTourDismissed();
      setWardTourDismissed(true);
    } else if (viewingOverlapMap) {
      writeOverlapTourDismissed();
      setOverlapTourDismissed(true);
    } else if (viewingEnrollmentMap) {
      writeEnrollmentTourDismissed();
      setEnrollmentTourDismissed(true);
    } else if (viewingQualityMap) {
      writeQualityTourDismissed();
      setQualityTourDismissed(true);
    } else if (viewingGeographicMap) {
      writeGeographicTourDismissed();
      setGeographicTourDismissed(true);
    } else {
      writeTourDismissed();
      setLegislativeTourDismissed(true);
    }
    setTourReplay(false);
    setTourStep(0);
  };

  const replayActiveTour = () => {
    setTourStep(0);
    setTourReplay(true);
    setIsFullscreen(false);
    setRailOpen(true);
  };

  const activeResource = activeItem?.type === 'resource' ? activeItem.resource : null;
  const isPdf = activeResource?.contentType === PDF_CONTENT_TYPE;
  // A documentation resource can be a static self-contained HTML file
  // (see generate_documentation_html.py) instead of a PDF - it's rendered
  // through the same signed-URL iframe path as maps rather than
  // SpreadsheetViewer (which is for CSV/data resources).
  const isHtmlDoc = activeResource?.kind === 'documentation' && activeResource?.contentType === HTML_CONTENT_TYPE;
  const isCalculator = activeResource?.kind === 'calculator';
  // Live maps (see LIVE_MAP_COMPONENTS) are rendered client-side from the
  // city_explorer database - they have no storagePath, so there is no signed
  // URL to fetch, download, reload, or "open in new tab".
  const isLiveMap = activeItem?.type === 'map' && Boolean(activeItem.file?.renderer === 'live');
  const liveMapComponent = isLiveMap ? getLiveMapComponent(activeItem.file.component) : null;
  const needsSignedUrl =
    (activeItem?.type === 'map' || isPdf || isHtmlDoc) && !isCalculator && !isLiveMap;

  const cacheKey = activeItem
    ? activeItem.type === 'resource'
      ? `res:${activeItem.resource.id}`
      : `map:${activeItem.category.id}/${activeItem.file.id}`
    : null;

  const loadView = useCallback(
    async ({ force = false } = {}) => {
      if (!activeItem || !needsSignedUrl) return;

      const cached = urlCache.current.get(cacheKey);
      setViewError(null);
      setUrlLoading(true);
      setMapFrameLoading(true);

      if (!force && cached && Date.now() - cached.fetchedAt < SIGNED_URL_TTL_MS) {
        setViewUrl(cached.url);
        setUrlLoading(false);
        return;
      }

      setViewUrl(null);
      try {
        const data =
          activeItem.type === 'resource'
            ? await fetchResourceUrl(activeItem.resource.id)
            : await fetchMapUrl(activeItem.category.id, activeItem.file.id);
        urlCache.current.set(cacheKey, { url: data.url, fetchedAt: Date.now() });
        setViewUrl(data.url);
      } catch (err) {
        setViewError(err.message || 'Failed to load this file');
        setMapFrameLoading(false);
      } finally {
        setUrlLoading(false);
      }
    },
    [activeItem, needsSignedUrl, cacheKey, fetchMapUrl, fetchResourceUrl]
  );

  useEffect(() => {
    loadView();
  }, [loadView]);

  useEffect(() => {
    urlCache.current.clear();
  }, [locationId]);

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const selectMap = (nextCategoryId, nextFileId) => {
    if (!nextFileId) return;
    navigate(`${basePath}/${nextCategoryId}/${nextFileId}`, { replace: true });
  };

  const selectCategory = (category) => {
    const files = filesForCategory(category);
    const currentBand = activeItem?.type === 'map' ? activeItem.file.gradeBand : null;
    const matchingBand = currentBand ? files.find((f) => f.gradeBand === currentBand) : null;
    selectMap(category.id, (matchingBand || files[0])?.id);
  };

  const selectResource = (resource) => {
    navigate(`${basePath}/documents/${resource.id}`, { replace: true });
  };

  const handleReload = () => {
    setReloadNonce((nonce) => nonce + 1);
    loadView({ force: true });
  };

  const handleDownload = async () => {
    if (!activeItem) return;
    try {
      const data =
        activeItem.type === 'resource'
          ? await fetchResourceUrl(activeItem.resource.id, 'attachment')
          : await fetchMapUrl(activeItem.category.id, activeItem.file.id, 'attachment');
      const anchor = document.createElement('a');
      anchor.href = data.url;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      setViewError(err.message || 'Failed to prepare the download');
    }
  };

  if (loadingLocation) {
    return (
      <Box sx={{ display: 'flex', height: `calc(100vh - ${footerOffsetPx}px)` }}>
        <Box sx={{ width: RAIL_WIDTH_PX, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', p: 2.5 }}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="85%" />
          <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 2, mt: 3 }} />
          <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 2, mt: 2 }} />
          <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 2, mt: 2 }} />
        </Box>
        <Skeleton variant="rectangular" sx={{ flexGrow: 1, height: '100%' }} />
      </Box>
    );
  }

  if (locationError) {
    return (
      <Box sx={{ p: 4, maxWidth: 720 }}>
        <Alert
          severity="error"
          action={
            showAllLocationsLink ? (
              <Button color="inherit" size="small" component={RouterLink} to={allLocationsPath}>
                All locations
              </Button>
            ) : null
          }
        >
          {locationError}
        </Alert>
      </Box>
    );
  }

  if (!location) return null;

  const metaLines = [partnerOrgName(location), location.county].filter(Boolean);

  const shellSx = isFullscreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => theme.zIndex.drawer + 2,
        bgcolor: 'background.paper',
      }
    : { height: `calc(100vh - ${footerOffsetPx}px)` };

  const showRail = railOpen && !isFullscreen;

  const activeTitle = isLanding
    ? analysisTitle(location.name)
    : activeItem?.type === 'resource'
      ? activeItem.resource.label
      : activeItem?.file?.label || 'Select a map';
  const activeSubtitle = isLanding
    ? 'Overview'
    : isCalculator
      ? 'Interactive scoring model'
      : activeItem?.type === 'resource'
        ? [activeItem.resource.fileName, formatBytes(activeItem.resource.sizeBytes)].filter(Boolean).join(' \u00b7 ')
        : activeItem?.category?.label || '';

  const renderResourceSections = (placement) =>
    RESOURCE_SECTIONS.filter((section) => section.placement === placement).map(
      ({ kind, title, icon: SectionIcon, collapsible }) => {
        const items = resources.filter((resource) => resource.kind === kind);
        if (!items.length) return null;
        return (
          <RailSection
            key={kind}
            title={title}
            collapsible={collapsible}
            open={Boolean(openSections[kind])}
            onToggle={() => toggleSection(kind)}
          >
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {items.map((resource) => {
                const isActive = activeResource?.id === resource.id;
                return (
                  <Box
                    key={resource.id}
                    onClick={() => !isActive && selectResource(resource)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: 1.5,
                      cursor: isActive ? 'default' : 'pointer',
                      bgcolor: isActive ? 'rgba(67, 56, 202, 0.08)' : 'transparent',
                      transition: 'background-color 0.2s ease-in-out',
                      '&:hover': { bgcolor: isActive ? 'rgba(67, 56, 202, 0.08)' : 'action.hover' },
                    }}
                  >
                    <SectionIcon
                      fontSize="small"
                      sx={{ color: isActive ? 'primary.main' : 'text.disabled', flexShrink: 0 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {resource.label}
                      </Typography>
                      {resource.sizeBytes ? (
                        <Typography variant="caption" color="text.disabled">
                          {formatBytes(resource.sizeBytes)}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </RailSection>
        );
      }
    );

  const renderContent = () => {
    if (isLanding) {
      return (
        <LocationLanding
          location={location}
          onSelectCategory={selectCategory}
          onSelectResource={selectResource}
          fetchSummary={fetchSummary}
        />
      );
    }

    if (!activeItem) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">This location has no published content yet.</Alert>
        </Box>
      );
    }

    if (viewError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleReload}>
                Try again
              </Button>
            }
          >
            {viewError}
          </Alert>
        </Box>
      );
    }

    if (activeItem.type === 'resource' && isCalculator) {
      const shareCalculator = onShareCalculator ? () => onShareCalculator(activeResource.id) : undefined;
      if (activeResource.calculatorType === 'risk') {
        return (
          <RiskCalculator embedded locationId={locationId} onShare={shareCalculator} isFullscreen={isFullscreen}
            riskMapPath={riskMapPath}
          />
        );
      }
      return (
        <OpportunityCalculator
          embedded
          locationId={locationId}
          includeDataSources={includeDataSourcesTour}
          isFullscreen={isFullscreen}
          onHighlightDataSources={setCalculatorHighlightSources}
          onShare={shareCalculator}
        />
      );
    }

    if (isLiveMap) {
      const LiveMapComponent = liveMapComponent;
      if (!LiveMapComponent) {
        return (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              {`Unknown live map component "${activeItem.file.component}".`}
            </Alert>
          </Box>
        );
      }
      return (
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <Suspense fallback={<ViewerLoading label={`Loading ${activeTitle.toLowerCase()}\u2026`} />}>
            <LiveMapComponent
              key={cacheKey}
              locationId={locationId}
              file={activeItem.file}
              riskCalculatorPath={riskCalculatorPath}
            />
          </Suspense>
        </Box>
      );
    }

    if (activeItem.type === 'resource' && !isPdf && !isHtmlDoc) {
      return (
        <SpreadsheetViewer
          key={activeItem.resource.id}
          locationId={locationId}
          resource={activeItem.resource}
          documentation={documentationResource}
          onOpenDocumentation={selectResource}
        />
      );
    }

    if (urlLoading || !viewUrl) {
      return <ViewerLoading label={`Loading ${activeTitle.toLowerCase()}\u2026`} />;
    }

    if (isPdf) {
      return <PdfViewer url={viewUrl} title={activeItem.resource.label} reloadNonce={reloadNonce} />;
    }

    return (
      <>
        {mapFrameLoading && (
          <ViewerLoading
            label={`Loading ${activeTitle.toLowerCase()}\u2026`}
            hint="Detailed maps can take a few seconds to render."
          />
        )}
        <iframe
          key={`${cacheKey}#${reloadNonce}`}
          title={activeTitle}
          src={viewUrl}
          onLoad={() => setMapFrameLoading(false)}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        />
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden', ...shellSx }}>
      {showRail && (
        <Box
          sx={{
            width: RAIL_WIDTH_PX,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflowY: 'auto',
            p: 2.5,
          }}
        >
          {showAllLocationsLink && (
            <Link
              component={RouterLink}
              to={allLocationsPath}
              underline="hover"
              color="text.secondary"
              sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.8125rem', mb: 1.5 }}
            >
              <ChevronLeftIcon fontSize="small" />
              All locations
            </Link>
          )}

          <Link
            component={RouterLink}
            to={basePath}
            underline={isLanding ? 'none' : 'hover'}
            color={isLanding ? 'text.primary' : 'inherit'}
            sx={{
              display: 'block',
              fontWeight: 700,
              lineHeight: 1.25,
              fontSize: '1.25rem',
              mb: 0,
              cursor: isLanding ? 'default' : 'pointer',
              '&:hover': { color: isLanding ? 'text.primary' : 'primary.main' },
            }}
          >
            {location.name}
          </Link>
          {metaLines.map((line, index) => (
            <Typography
              key={line}
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: index === 0 ? 0.5 : 0 }}
            >
              {line}
            </Typography>
          ))}

          {renderResourceSections('beforeMaps')}

          {categories.length > 0 && (
            <RailSection
              title="Maps"
              collapsible
              open={Boolean(openSections[MAPS_SECTION_KEY])}
              onToggle={() => toggleSection(MAPS_SECTION_KEY)}
            >
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {categories.map((category) => {
                  const isActive = activeItem?.type === 'map' && category.id === activeItem.category.id;
                  const files = filesForCategory(category);
                  return (
                    <Paper
                      key={category.id}
                      elevation={0}
                      onClick={() => !isActive && selectCategory(category)}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: isActive ? 'primary.main' : 'divider',
                        bgcolor: isActive ? 'rgba(67, 56, 202, 0.04)' : 'background.paper',
                        borderRadius: 2,
                        cursor: isActive ? 'default' : 'pointer',
                        transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
                        '&:hover': { borderColor: 'primary.main' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <MapOutlinedIcon
                          fontSize="small"
                          sx={{ color: isActive ? 'primary.main' : 'text.disabled', mt: '2px' }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: isActive ? 'primary.main' : 'text.primary' }}
                        >
                          {category.label}
                        </Typography>
                      </Box>

                      {isActive && (
                        <>
                          {category.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              {category.description}
                            </Typography>
                          )}

                          {isGradeBanded(category) ? (
                            <ToggleButtonGroup
                              value={activeItem.file.id}
                              exclusive
                              size="small"
                              fullWidth
                              onChange={(event, nextFileId) => selectMap(category.id, nextFileId)}
                              sx={{ mt: 1.5 }}
                            >
                              {files.map((file) => (
                                <ToggleButton key={file.id} value={file.id} sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                  {file.shortLabel || file.label}
                                </ToggleButton>
                              ))}
                            </ToggleButtonGroup>
                          ) : (
                            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                              {files.map((file) => (
                                <Button
                                  key={file.id}
                                  size="small"
                                  variant={file.id === activeItem.file.id ? 'contained' : 'outlined'}
                                  disableElevation
                                  onClick={() => selectMap(category.id, file.id)}
                                  sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  {file.label}
                                </Button>
                              ))}
                            </Stack>
                          )}
                        </>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </RailSection>
          )}

          <Box
            data-testid="city-explorer-sources-rail"
            sx={
              highlightSourcesRail
                ? {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    borderRadius: 2,
                    px: 0.5,
                    pb: 1,
                    mt: 1,
                  }
                : undefined
            }
          >
            {renderResourceSections('afterMaps')}
          </Box>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {!isFullscreen && (
              <Tooltip title={railOpen ? 'Hide panel' : 'Show panel'}>
                <IconButton size="small" onClick={() => setRailOpen((open) => !open)}>
                  {railOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Tooltip>
            )}
            {!isLanding && (
              <Tooltip title={`Back to ${analysisTitle(location.name)}`}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  onClick={() => {
                    if (isFullscreen) setIsFullscreen(false);
                    navigate(basePath);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Overview
                </Button>
              </Tooltip>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                {activeTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {isFullscreen ? `${location.name} \u00b7 ` : ''}
                {activeSubtitle}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            {toolbarExtra}
            {!isCalculator && !isLiveMap && (
              <Tooltip title="Download">
                <span>
                  <IconButton size="small" onClick={handleDownload} disabled={!activeItem}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {showReplayButton && (
              <Tooltip title="See the map tour again">
                <Button
                  size="small"
                  startIcon={<HelpOutlineIcon fontSize="small" />}
                  onClick={replayActiveTour}
                  aria-label="Replay map tour"
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Tour
                </Button>
              </Tooltip>
            )}
            {needsSignedUrl && (
              <>
                <Tooltip title="Reload">
                  <span>
                    <IconButton size="small" onClick={handleReload} disabled={urlLoading}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Open in new tab">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!viewUrl}
                      onClick={() => viewUrl && window.open(viewUrl, '_blank', 'noopener,noreferrer')}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Tooltip title={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'}>
              <IconButton size="small" onClick={() => setIsFullscreen((full) => !full)}>
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            position: 'relative',
            bgcolor: isLanding || isCalculator ? 'background.default' : 'grey.200',
            overflow: isLanding || isCalculator ? 'auto' : 'hidden',
          }}
        >
          {renderContent()}
          {showLegislativeTour && (
            <LegislativeMapTour
              steps={legislativeSteps}
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissActiveTour}
            />
          )}
          {showDesertTour && (
            <LegislativeMapTour
              steps={desertSteps}
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissActiveTour}
              testId="desert-map-tour"
            />
          )}
          {showWardTour && (
            <LegislativeMapTour
              steps={wardSteps}
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissActiveTour}
              testId="ward-map-tour"
            />
          )}
          {showOverlapTour && (
            <LegislativeMapTour
              steps={overlapSteps}
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissActiveTour}
              testId="overlap-map-tour"
            />
          )}
          {showEnrollmentTour && (
            <LegislativeMapTour
              steps={ENROLLMENT_TOUR_STEPS}
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissActiveTour}
              testId="enrollment-map-tour"
            />
          )}
          {showQualityTour && (
            <LegislativeMapTour
              steps={QUALITY_TOUR_STEPS}
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissActiveTour}
              testId="quality-map-tour"
            />
          )}
          {showGeographicTour && (
            <LegislativeMapTour
              steps={geographicSteps}
              step={tourStep}
              onStepChange={setTourStep}
              onDismiss={dismissActiveTour}
              testId="geographic-map-tour"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExplorerView;
